import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import AsyncSelect from 'react-select/async';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';
import scanAssetUrl from '../../../lib/scanAssetUrl';

const STATUS_BADGE = {
  matched: 'bg-green-100 text-green-800',
  candidates: 'bg-amber-100 text-amber-800',
  unresolved: 'bg-red-100 text-red-800',
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const cls = STATUS_BADGE[value] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${cls}`}>
      {value}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="col-span-2 text-sm text-gray-800 break-words">{children}</dd>
    </div>
  );
}

function fmtScore(s) {
  if (s === null || s === undefined) return '—';
  return (Number(s) || 0).toFixed(3);
}

// Render a single candidate row. Shared by the original-candidates table
// and the rerun "now" table so the column layout stays in sync.
function CandidateRow({ c, highlightBond = false, mark = null }) {
  const lib = c.libation || {};
  const listing = c.listing || lib.listing;
  return (
    <tr className={highlightBond ? 'bg-green-50' : ''}>
      <td className="px-2 py-1 text-xs">
        {mark && (
          <span className={`mr-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
            mark === 'new' ? 'bg-green-100 text-green-700'
              : mark === 'gone' ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600'
          }`}>{mark}</span>
        )}
        {lib.id ? (
          <Link
            to={listing?.id ? `/admin/listings/${listing.id}/wines/${lib.id}` : '#'}
            className="text-icon hover:underline font-medium"
          >
            {lib.name || '(unnamed)'}
          </Link>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-2 py-1 text-xs text-gray-700">
        {listing?.name || lib.listingName || <span className="text-gray-400">—</span>}
      </td>
      <td className="px-2 py-1 text-xs text-gray-700 tabular-nums">{fmtScore(c.listingConfidence)}</td>
      <td className="px-2 py-1 text-xs text-gray-700 tabular-nums">{fmtScore(c.wineConfidence)}</td>
      <td className="px-2 py-1 text-xs text-gray-700">
        {c.vintageExists === true ? '✓' : c.vintageExists === false ? '—' : ''}
        {c.year ? ` ${c.year === 9999 ? 'NV' : c.year}` : ''}
      </td>
    </tr>
  );
}

export default function BottleScanDetail() {
  const { scanId } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rerun, setRerun] = useState(null);
  const [rerunLoading, setRerunLoading] = useState(false);
  const [overrideLV, setOverrideLV] = useState(null);
  const [overriding, setOverriding] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.service('admin/libation-label-scan').get(scanId);
      setScan(result);
    } catch (err) {
      setError(err.message || 'Failed to load scan');
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => { load(); }, [load]);

  const doRerun = async () => {
    setRerunLoading(true);
    try {
      const result = await client.service('admin/libation-label-scan-rerun').create({ scanId });
      setRerun(result);
    } catch (err) {
      setError(err.message || 'Rerun failed');
    } finally {
      setRerunLoading(false);
    }
  };

  const doOverride = async () => {
    if (!overrideLV) return;
    const confirmed = window.confirm(
      `Re-bond this scan to ${overrideLV.label}? The user's original choice (${scan.libationVintageId || 'none'}) will be replaced.`
    );
    if (!confirmed) return;

    setOverriding(true);
    setOverrideMessage(null);
    try {
      await client.service('admin/libation-label-scan').patch(scanId, {
        libationVintageId: overrideLV.value,
        matchResult: 'matched',
      });
      setOverrideMessage(`Re-bonded to ${overrideLV.label}.`);
      setOverrideLV(null);
      await load();
    } catch (err) {
      setOverrideMessage(err.message || 'Override failed');
    } finally {
      setOverriding(false);
    }
  };

  // libationVintage picker for override. Mirrors the multi-token AND-of-iLike
  // pattern from the listing picker.
  const loadLVOptions = async (input) => {
    if (!input || input.length < 2) return [];
    const tokens = input.trim().split(/\s+/).filter(Boolean);
    const andClauses = tokens.map((t) => ({ name: { $iLike: `%${t}%` } }));
    const result = await client.service('admin/libation').find({
      query: {
        $and: andClauses,
        $limit: 20,
        $sort: { name: 1 },
      },
    });
    const libations = result.data || result;
    const opts = [];
    for (const lib of libations) {
      const vintages = lib.libationVintages || [];
      if (vintages.length === 0) {
        opts.push({
          value: null,
          label: `${lib.name} — no vintages`,
          disabled: true,
        });
      } else {
        for (const lv of vintages) {
          const yr = lv.vintage?.year;
          opts.push({
            value: lv.id,
            label: `${lib.name} · ${yr === 9999 ? 'NV' : yr || '?'}${lib.listing?.name ? ` · ${lib.listing.name}` : ''}`,
          });
        }
      }
    }
    return opts;
  };

  // Diff between original and rerun candidates so the UI can mark new /
  // gone / kept rows. Keyed by libationId since the same libation may have
  // a different vintage decision across runs.
  const diff = useMemo(() => {
    if (!rerun) return null;
    const originalIds = new Set((rerun.original.candidates || []).map((c) => c.libationId));
    const currentIds = new Set((rerun.current.candidates || []).map((c) => c.libationId));
    return {
      originalMark: (c) => (currentIds.has(c.libationId) ? null : 'gone'),
      currentMark: (c) => (originalIds.has(c.libationId) ? null : 'new'),
    };
  }, [rerun]);

  if (loading && !scan) {
    return <Page title="Bottle Scan">Loading…</Page>;
  }
  if (error && !scan) {
    return <Page title="Bottle Scan"><div className="text-red-700">{error}</div></Page>;
  }
  if (!scan) return null;

  const ex = scan.extractedData || {};
  const lv = scan.libationVintage;
  const lib = lv?.libation;
  const listing = lib?.listing;
  const photoLarge = scanAssetUrl(scan, '?w=1200&h=1200&f=inside&q=95');
  const photoOriginal = scan.photoUrl;
  const originalCandidates = scan.alternateResults?.candidates || [];

  return (
    <Page title="Bottle Scan">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Bottle Scans', href: '/admin/reports/bottle-scans' },
          { name: scanId.slice(0, 8) },
        ]} />
        <div className="mt-2 flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Scan {scanId.slice(0, 8)}</h1>
          <div className="flex gap-2 items-center">
            <StatusBadge value={scan.matchResult} />
            <span className="text-sm text-gray-500">
              {format(new Date(scan.createdAt), 'MMM d, yyyy h:mm a')}
            </span>
          </div>
        </div>
        {scan.user && (
          <div className="mt-1 text-sm text-gray-600">
            by{' '}
            <Link to={`/admin/users/${scan.user.id}`} className="text-icon hover:underline">
              {scan.user.firstName} {scan.user.lastName}
            </Link>
            {scan.user.email && <span className="text-gray-400"> · {scan.user.email}</span>}
            {scan.user.role && <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 rounded">{scan.user.role}</span>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Photo */}
        <section className="bg-white shadow-sm rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Photo</h2>
          {photoLarge ? (
            <a href={photoOriginal || photoLarge} target="_blank" rel="noreferrer">
              <img
                src={photoLarge}
                alt="bottle scan"
                className="w-full rounded border border-gray-200"
                onError={(e) => { e.currentTarget.alt = 'photo not available'; }}
              />
            </a>
          ) : (
            <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-gray-400">
              No photo
            </div>
          )}
          <div className="mt-2 text-xs text-gray-500">
            Quality: <span className="text-gray-700">{scan.photoQuality || '—'}</span>
          </div>
        </section>

        {/* AI Extraction */}
        <section className="bg-white shadow-sm rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">AI Extraction</h2>
          <dl className="divide-y divide-gray-100">
            <Field label="Name">{ex.name || <span className="text-gray-400">—</span>}</Field>
            <Field label="Producer">{ex.producer || <span className="text-gray-400">—</span>}</Field>
            <Field label="Vintage">{ex.vintage || <span className="text-gray-400">NV / unknown</span>}</Field>
            <Field label="Varietal">{ex.varietal || <span className="text-gray-400">—</span>}</Field>
            <Field label="Type">{ex.type || <span className="text-gray-400">—</span>}</Field>
            <Field label="ABV">{ex.abv ? `${ex.abv}%` : <span className="text-gray-400">—</span>}</Field>
            <Field label="Region">{ex.region || <span className="text-gray-400">—</span>}</Field>
            {ex.tastingNotes && <Field label="Tasting notes">{ex.tastingNotes}</Field>}
            {ex.aging && <Field label="Aging">{ex.aging}</Field>}
            <Field label="Prominent text">
              {Array.isArray(ex.prominentText) && ex.prominentText.length > 0 ? (
                <ul className="space-y-0.5">
                  {ex.prominentText.map((p, i) => (
                    <li key={`prom-${i}`} className="text-xs">
                      <span className="font-mono text-gray-500 mr-2">{p.prominence?.toFixed?.(1) || '?'}</span>
                      {p.text}
                    </li>
                  ))}
                </ul>
              ) : <span className="text-gray-400">—</span>}
            </Field>
            {ex.rawOcrText && (
              <Field label="Raw OCR">
                <details>
                  <summary className="cursor-pointer text-xs text-gray-500">show</summary>
                  <pre className="mt-1 text-xs text-gray-700 whitespace-pre-wrap">{ex.rawOcrText}</pre>
                </details>
              </Field>
            )}
          </dl>
        </section>
      </div>

      {/* User's final choice */}
      <section className="bg-white shadow-sm rounded-lg p-4 mt-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">User&apos;s final choice</h2>
        {lib ? (
          <div className="text-sm">
            <Link
              to={listing ? `/admin/listings/${listing.id}/wines/${lib.id}` : '#'}
              className="text-icon hover:underline font-medium text-base"
            >
              {lib.name}
            </Link>
            {' '}
            <span className="text-gray-500">
              · {lv?.vintage?.year === 9999 ? 'NV' : lv?.vintage?.year || '?'}
              {listing && ` · ${listing.name}`}
              {lib.varietal && ` · ${lib.varietal.name}`}
            </span>
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            User did not map this scan. <StatusBadge value={scan.matchResult} />
          </div>
        )}
      </section>

      {/* Original candidates */}
      <section className="bg-white shadow-sm rounded-lg p-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">
            Original match candidates ({originalCandidates.length})
          </h2>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium border border-icon text-icon rounded hover:bg-red-50 disabled:opacity-50"
            onClick={doRerun}
            disabled={rerunLoading}
          >
            {rerunLoading ? 'Running…' : (rerun ? 'Re-run again' : 'Re-run matcher')}
          </button>
        </div>
        {originalCandidates.length === 0 ? (
          <div className="text-sm text-gray-500">No candidates stored — scan was auto-bonded or unresolved.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Wine</th>
                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Winery</th>
                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Listing</th>
                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Wine</th>
                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Vintage</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {originalCandidates.map((c) => (
                <CandidateRow
                  key={`orig-${c.libationId}`}
                  c={c}
                  highlightBond={c.libationId === lib?.id}
                  mark={diff?.originalMark(c)}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Rerun results */}
      {rerun && (
        <section className="bg-white shadow-sm rounded-lg p-4 mt-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Today&apos;s match candidates ({rerun.current.candidates.length})
          </h2>
          {rerun.current.topAutoMatch && (
            <div className="mb-2 text-xs text-green-700">
              Today this would auto-bond to{' '}
              <span className="font-medium">{rerun.current.topAutoMatch.libation?.name}</span>{' '}
              ({rerun.current.topAutoMatch.wineConfidence.toFixed(3)})
            </div>
          )}
          {rerun.current.candidates.length === 0 ? (
            <div className="text-sm text-gray-500">The current matcher returns no candidates for this scan.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Wine</th>
                  <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Winery</th>
                  <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Listing</th>
                  <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Wine</th>
                  <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Vintage</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rerun.current.candidates.map((c) => (
                  <CandidateRow key={`rerun-${c.libationId}`} c={c} mark={diff?.currentMark(c)} />
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* Override mapping */}
      <section className="bg-white shadow-sm rounded-lg p-4 mt-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Override mapping</h2>
        <p className="text-xs text-gray-500 mb-2">
          Re-bond this scan to a different libationVintage. The change is logged but does not retroactively
          modify the user&apos;s cellar entries or sips.
        </p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <AsyncSelect
              loadOptions={loadLVOptions}
              value={overrideLV}
              onChange={setOverrideLV}
              placeholder="Search libations (e.g. 'trisaetum estates')"
              isClearable
              isOptionDisabled={(o) => o.disabled}
            />
          </div>
          <button
            type="button"
            onClick={doOverride}
            disabled={!overrideLV || overriding}
            className="px-4 py-2 text-sm font-medium bg-icon text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {overriding ? 'Saving…' : 'Apply override'}
          </button>
        </div>
        {overrideMessage && (
          <div className="mt-2 text-sm text-gray-700">{overrideMessage}</div>
        )}
      </section>

      {/* Metadata footer */}
      <section className="bg-white shadow-sm rounded-lg p-4 mt-6 mb-8">
        <details>
          <summary className="text-sm font-semibold text-gray-700 cursor-pointer">Metadata</summary>
          <dl className="mt-3 divide-y divide-gray-100">
            <Field label="Model">{scan.modelId || '—'}</Field>
            <Field label="Input tokens">{scan.inputTokens ?? '—'}</Field>
            <Field label="Output tokens">{scan.outputTokens ?? '—'}</Field>
            <Field label="Processing time">{scan.processingTimeMs ? `${scan.processingTimeMs}ms` : '—'}</Field>
            <Field label="Scan source">{scan.scanSource || '—'}</Field>
            <Field label="Primary label">{scan.isPrimaryLabel ? 'Yes' : 'No'}</Field>
            <Field label="Status">{scan.status}</Field>
            <Field label="Scan ID"><code className="text-xs">{scan.id}</code></Field>
          </dl>
        </details>
      </section>
    </Page>
  );
}
